import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { ChevronLeft, ChevronRight, Plus, Trash2, Edit2, X } from "lucide-react";
import { InitialsBadge } from "../../components/DashboardLayout";
import { toast, Toaster } from "sonner";

const HOURS = Array.from({ length: 13 }, (_, i) => i + 7); // 7..19
const DAY_LABELS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const TYPE_COLORS = {
    parcoursup: "bg-ice/20 border-ice/50 text-ice",
    specialites: "bg-emerald-400/15 border-emerald-400/40 text-emerald-300",
    bilan: "bg-amber-400/15 border-amber-400/40 text-amber-200",
    reorientation: "bg-pink-400/15 border-pink-400/40 text-pink-200",
    suivi: "bg-blue-400/15 border-blue-400/40 text-blue-200",
    decouverte: "bg-purple-400/15 border-purple-400/40 text-purple-200",
};

function startOfWeek(d) {
    const date = new Date(d);
    const day = (date.getDay() + 6) % 7;
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - day);
    return date;
}
function fmtRange(start) {
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const opt = { day: "numeric", month: "long" };
    return `${start.toLocaleDateString("fr-FR", opt)} — ${end.toLocaleDateString("fr-FR", opt)} ${end.getFullYear()}`;
}

export default function Calendrier() {
    const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
    const [filter, setFilter] = useState("all");
    const [appts, setAppts] = useState([]);
    const [clients, setClients] = useState([]);
    const [selected, setSelected] = useState(null);
    const [showNew, setShowNew] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        try {
            const [a, c] = await Promise.all([
                api.get("/appointments", { params: filter !== "all" ? { consultant: filter } : {} }),
                api.get("/clients"),
            ]);
            setAppts(a.data);
            setClients(c.data);
        } catch {
            toast.error("Erreur de chargement");
        }
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [filter]);

    const weekAppts = useMemo(() => {
        const end = new Date(weekStart);
        end.setDate(end.getDate() + 7);
        return appts.filter((a) => {
            const d = new Date(a.starts_at);
            return d >= weekStart && d < end;
        });
    }, [appts, weekStart]);

    const days = useMemo(
        () => Array.from({ length: 7 }, (_, i) => {
            const d = new Date(weekStart);
            d.setDate(d.getDate() + i);
            return d;
        }),
        [weekStart]
    );

    const removeAppt = async (id) => {
        if (!window.confirm("Supprimer ce rendez-vous ?")) return;
        try {
            await api.delete(`/appointments/${id}`);
            setSelected(null);
            load();
            toast.success("Rendez-vous supprimé");
        } catch {
            toast.error("Erreur");
        }
    };

    return (
        <div data-testid="dashboard-calendrier" className="space-y-6">
            <Toaster position="top-center" theme="dark" richColors />
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <span className="text-xs tracking-[0.3em] text-ice uppercase">— Calendrier</span>
                    <h1 className="font-serif text-4xl mt-2">Agenda</h1>
                </div>
                <button
                    data-testid="btn-new-appt"
                    onClick={() => setShowNew(true)}
                    className="inline-flex items-center gap-2 bg-ice text-ink-900 hover:bg-white transition-colors px-5 py-2.5 rounded-md text-sm font-medium"
                >
                    <Plus size={16} /> Nouveau rendez-vous
                </button>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 bg-ink-600 border border-white/10 rounded-md p-1">
                    <button
                        onClick={() => {
                            const n = new Date(weekStart);
                            n.setDate(n.getDate() - 7);
                            setWeekStart(n);
                        }}
                        className="p-2 rounded hover:bg-ink-700"
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <div className="text-sm px-3 min-w-[260px] text-center">{fmtRange(weekStart)}</div>
                    <button
                        onClick={() => {
                            const n = new Date(weekStart);
                            n.setDate(n.getDate() + 7);
                            setWeekStart(n);
                        }}
                        className="p-2 rounded hover:bg-ink-700"
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>
                <div className="flex gap-2">
                    {[
                        ["all", "Tous"],
                        ["CC", "CC"],
                        ["CCJ", "CCJ"],
                    ].map(([v, l]) => (
                        <button
                            key={v}
                            onClick={() => setFilter(v)}
                            data-testid={`cal-filter-${v}`}
                            className={`px-4 py-2 rounded-md text-sm border transition-colors ${
                                filter === v ? "bg-ice text-ink-900 border-ice" : "border-white/15 text-white/75 hover:border-ice/40"
                            }`}
                        >
                            {l}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                <div className="lg:col-span-3 bg-ink-600 border border-white/10 rounded-xl overflow-hidden">
                    <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-white/10 text-xs text-white/55">
                        <div></div>
                        {days.map((d, i) => (
                            <div key={i} className="px-3 py-3 text-center">
                                <div className="uppercase tracking-wider">{DAY_LABELS[i]}</div>
                                <div className="text-white text-base font-serif">{d.getDate()}</div>
                            </div>
                        ))}
                    </div>
                    <div className="grid grid-cols-[60px_repeat(7,1fr)]">
                        {HOURS.map((h) => (
                            <React.Fragment key={h}>
                                <div className="text-[10px] text-white/40 px-2 py-2 text-right border-b border-white/5">
                                    {h}h
                                </div>
                                {days.map((d, di) => {
                                    const slotAppts = weekAppts.filter((a) => {
                                        const x = new Date(a.starts_at);
                                        return x.getDate() === d.getDate() && x.getMonth() === d.getMonth() && x.getHours() === h;
                                    });
                                    return (
                                        <div key={di} className="border-l border-b border-white/5 min-h-[44px] p-1 relative">
                                            {slotAppts.map((a) => (
                                                <button
                                                    key={a.id}
                                                    onClick={() => setSelected(a)}
                                                    data-testid={`appt-${a.id}`}
                                                    className={`block w-full text-left text-[11px] rounded-md border px-2 py-1 mb-1 hover:opacity-90 transition ${
                                                        TYPE_COLORS[a.type] || "bg-white/10 border-white/20"
                                                    }`}
                                                >
                                                    <div className="font-medium truncate">{a.title}</div>
                                                    <div className="opacity-70 capitalize">{a.format}</div>
                                                </button>
                                            ))}
                                        </div>
                                    );
                                })}
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                <aside className="space-y-5">
                    <section className="bg-ink-600 border border-white/10 rounded-xl p-5">
                        <h3 className="font-serif text-lg mb-3">Cette semaine</h3>
                        <div className="text-3xl font-serif text-ice">{weekAppts.length}</div>
                        <div className="text-xs text-white/55 uppercase tracking-wider mt-1">Rendez-vous</div>
                    </section>

                    {selected ? (
                        <section className="bg-ink-600 border border-ice/30 rounded-xl p-5 space-y-3">
                            <div className="flex items-center justify-between">
                                <h3 className="font-serif text-lg">Sélection</h3>
                                <button onClick={() => setSelected(null)} className="text-white/50 hover:text-white">
                                    <X size={16} />
                                </button>
                            </div>
                            <div className="text-sm">
                                <div className="font-medium">{selected.title}</div>
                                <div className="text-white/55 mt-1">
                                    {new Date(selected.starts_at).toLocaleString("fr-FR", {
                                        weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit",
                                    })}
                                </div>
                                <div className="mt-2 flex items-center gap-2">
                                    <span className="text-xs uppercase tracking-wider text-white/50">{selected.type}</span>
                                    <span className="text-xs uppercase tracking-wider text-white/50">· {selected.format}</span>
                                </div>
                                <div className="mt-3 flex items-center gap-2">
                                    <span className="text-xs text-white/55">Suivi par</span>
                                    <InitialsBadge value={selected.consultant} size="sm" tone="ice" />
                                </div>
                            </div>
                            <div className="flex gap-2 pt-2">
                                <button
                                    onClick={() => setEditing(selected)}
                                    data-testid="btn-edit-appt"
                                    className="flex-1 inline-flex items-center justify-center gap-1.5 text-sm bg-ink-800 border border-white/15 hover:border-ice rounded-md py-2"
                                >
                                    <Edit2 size={14} /> Modifier
                                </button>
                                <button
                                    onClick={() => removeAppt(selected.id)}
                                    data-testid="btn-delete-appt"
                                    className="flex-1 inline-flex items-center justify-center gap-1.5 text-sm bg-red-500/10 border border-red-400/30 text-red-300 hover:bg-red-500/20 rounded-md py-2"
                                >
                                    <Trash2 size={14} /> Supprimer
                                </button>
                            </div>
                        </section>
                    ) : (
                        <section className="bg-ink-600 border border-white/10 rounded-xl p-5 text-sm text-white/55">
                            Sélectionnez un rendez-vous dans le calendrier pour voir le détail.
                        </section>
                    )}
                </aside>
            </div>

            {(showNew || editing) && (
                <ApptForm
                    clients={clients}
                    initial={editing}
                    onClose={() => {
                        setShowNew(false);
                        setEditing(null);
                    }}
                    onSaved={() => {
                        setShowNew(false);
                        setEditing(null);
                        setSelected(null);
                        load();
                    }}
                />
            )}
        </div>
    );
}

function ApptForm({ clients, initial, onClose, onSaved }) {
    const editing = !!initial;
    const def = initial
        ? {
              client_id: initial.client_id,
              type: initial.type,
              format: initial.format,
              consultant: initial.consultant,
              date: new Date(initial.starts_at).toISOString().slice(0, 10),
              time: new Date(initial.starts_at).toISOString().slice(11, 16),
              duration_min: initial.duration_min,
          }
        : {
              client_id: clients[0]?.id || "",
              type: "suivi",
              format: "visio",
              consultant: "CC",
              date: new Date().toISOString().slice(0, 10),
              time: "10:00",
              duration_min: 60,
          };
    const [form, setForm] = useState(def);
    const [submitting, setSubmitting] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const starts_at = new Date(`${form.date}T${form.time}:00`).toISOString();
        const payload = {
            client_id: form.client_id,
            type: form.type,
            format: form.format,
            consultant: form.consultant,
            starts_at,
            duration_min: Number(form.duration_min),
        };
        try {
            if (editing) await api.patch(`/appointments/${initial.id}`, payload);
            else await api.post("/appointments", payload);
            toast.success(editing ? "Rendez-vous modifié" : "Rendez-vous ajouté");
            onSaved();
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Erreur");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-ink-700 rounded-xl border border-ice/20 max-w-md w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between">
                    <h3 className="font-serif text-2xl">{editing ? "Modifier le rendez-vous" : "Nouveau rendez-vous"}</h3>
                    <button onClick={onClose} className="text-white/50 hover:text-white">
                        <X size={18} />
                    </button>
                </div>
                <form onSubmit={submit} className="space-y-3">
                    <Field label="Client">
                        <select
                            value={form.client_id}
                            onChange={(e) => set("client_id", e.target.value)}
                            required
                            className={selectCls}
                        >
                            {clients.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.prenom} {c.nom} — {c.niveau}
                                </option>
                            ))}
                        </select>
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Date">
                            <input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className={selectCls} required />
                        </Field>
                        <Field label="Heure">
                            <input type="time" value={form.time} onChange={(e) => set("time", e.target.value)} className={selectCls} required />
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Type">
                            <select value={form.type} onChange={(e) => set("type", e.target.value)} className={selectCls}>
                                <option value="suivi">Suivi</option>
                                <option value="parcoursup">Parcoursup</option>
                                <option value="specialites">Spécialités</option>
                                <option value="bilan">Bilan</option>
                                <option value="reorientation">Réorientation</option>
                                <option value="decouverte">Découverte</option>
                            </select>
                        </Field>
                        <Field label="Format">
                            <select value={form.format} onChange={(e) => set("format", e.target.value)} className={selectCls}>
                                <option value="visio">Visio</option>
                                <option value="presentiel">Présentiel</option>
                                <option value="cafe">Café</option>
                                <option value="telephone">Téléphone</option>
                            </select>
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Consultant">
                            <select value={form.consultant} onChange={(e) => set("consultant", e.target.value)} className={selectCls}>
                                <option value="CC">CC — Cécile</option>
                                <option value="CCJ">CCJ — Cassandre</option>
                            </select>
                        </Field>
                        <Field label="Durée (min)">
                            <input type="number" min={15} step={15} value={form.duration_min} onChange={(e) => set("duration_min", e.target.value)} className={selectCls} />
                        </Field>
                    </div>
                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="btn-submit-appt"
                        className="w-full bg-ice text-ink-900 hover:bg-white disabled:opacity-60 transition-colors px-6 py-3 rounded-md font-medium mt-2"
                    >
                        {submitting ? "Enregistrement…" : editing ? "Enregistrer" : "Ajouter le rendez-vous"}
                    </button>
                </form>
            </div>
        </div>
    );
}

const selectCls =
    "w-full bg-ink-800 border border-white/15 text-white focus:border-ice focus:ring-1 focus:ring-ice rounded-md px-3 py-2 text-sm outline-none";

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-xs tracking-[0.2em] text-white/60 uppercase">{label}</span>
            <div className="mt-1.5">{children}</div>
        </label>
    );
}
